import React from 'react';
import { Redirect } from 'react-router';
import { Route } from 'react-router-dom';

import * as PagePaths from '../constants/pagePath';
import {
  StripOrganizationPaymentCancelFeedback,
  StripOrganizationPaymentSuccessFeedback,
} from '../pages/organization/StripFeedbackPages';
import { LegacyRedirect } from './linkablePage';
import { ROUTE_PATTERNS } from './linkableRoutes';
import { MenuOptions, PrivateRoute, PublicAndPrivateRoute } from './Util';

// lazy load all the views
// auth
const SignIn = React.lazy(() => import('../pages/auth/SignIn'));
const SignUp = React.lazy(() => import('../pages/auth/SignUp'));
const Home = React.lazy(() => import('../pages/home/index'));
const Register = React.lazy(() => import('../pages/auth/register/index'));
const Logout = React.lazy(() => import('../pages/auth/Logout'));
const ForgetPassword = React.lazy(() => import('../pages/auth/ForgetPassword'));
const ForgotPassword = React.lazy(() => import('../pages/auth/ForgotPassword'));
// auth register error pages
const ErrorPage = React.lazy(() => import('../pages/auth/register/components/ErrorPage'));
// dashboard
const Dashboard = React.lazy(() => import('../pages/dashboard'));
// bulk operations
const BulkOperations = React.lazy(() => import('../pages/bulkOperations/BulkOperations'));
// client
const ClientList = React.lazy(() => import('../pages/clients/ClientList'));
const AddClient = React.lazy(() => import('../pages/clients/AddClient'));
const EditClient = React.lazy(() => import('../pages/clients/EditClient'));
// value proposition
const ValuePropositionsList = React.lazy(() =>
  import('../pages/valuePropositions/ValuePropositionsList')
);
const AddValueProposition = React.lazy(() =>
  import('../pages/valuePropositions/AddValueProposition')
);
const EditValueProposition = React.lazy(() =>
  import('../pages/valuePropositions/EditValueProposition')
);
const SharedValueProposition = React.lazy(() =>
  import('../pages/valuePropositions/SharedValueProposition')
);
const SharedUserProfile = React.lazy(() => import('../pages/vpx/SharedUserProfile'));
// fundamentals
const FundamentalsList = React.lazy(() => import('../pages/fundamentals/FundamentalsList'));
const AddFundamentals = React.lazy(() => import('../pages/fundamentals/AddFundament'));
const EditFundamentals = React.lazy(() => import('../pages/fundamentals/EditFundament'));
// profile
const ProfileSecurity = React.lazy(() => import('../pages/profile/Security'));
const PersonalInformation = React.lazy(() => import('../pages/profile/PersonalInformation'));
const Subscription = React.lazy(() => import('../pages/profile/Subscription'));
const PaymentSuccess = React.lazy(() => import('../pages/profile/PaymentSuccess'));
const SubscriptionExpiredPage = React.lazy(() =>
  import('../pages/subscriptionExpired/SubscriptionExpiredPage')
);
// organization
const OrganizationDashboard = React.lazy(() => import('../pages/organization/Dashboard'));
const EditOrganizationDetails = React.lazy(() =>
  import('../pages/organization/EditOrganizationDetails')
);

const ListOrganizationClients = React.lazy(() => import('../pages/organization/ListClients'));
const AddOrganizationClient = React.lazy(() =>
  import('../pages/organization/AddOrganizationClient')
);
const EditOrganizationClient = React.lazy(() =>
  import('../pages/organization/EditOrganizationClient')
);
const ConfirmOrganizationInvitation = React.lazy(() =>
  import('../pages/organization/ConfirmOrganizationInvitation')
);

const ListUsers = React.lazy(() => import('../pages/organization/ListUsers'));
const CreateOrganization = React.lazy(() => import('../pages/organization/CreateOrganization'));
const CreateOrganizationRedirect = React.lazy(() =>
  import('../components/CreateOrganizationRedirect')
);
const OrganizationValupPropositionList = React.lazy(() =>
  import('../pages/organization/ValuePropositionList')
);
const LicenseChangeConfirm = React.lazy(() => import('../pages/organization/LicenseChangeConfirm'));
const ListOrganizationInvites = React.lazy(() => import('../pages/organization/ListInvites'));
const ReactivateOrganization = React.lazy(() => import('../pages/organization/ReactivateOrganization'));

// VPX
const Vpxlist = React.lazy(() => import('../pages/vpx/VpxList'));
const VpxOrganizationProfile = React.lazy(() => import('../pages/vpx/VpxOrganizationProfile'));
const VpxUserProfile = React.lazy(() => import('../pages/vpx/VpxUserProfile'));

const VpxPublicInvitation = React.lazy(() => import('../pages/vpx/ExternalPublicList'));
const VpxPrivateInvitation = React.lazy(() => import('../pages/vpx/ExternalPrivateList'));

// VPX Invitation
const VpxInvitationList = React.lazy(() => import('../pages/vpx-invitation/VpxInvitationList'));

const InviteUsers = React.lazy(() => import('../pages/admin/InviteUsers'));
const SystemAdmin = React.lazy(() => import('../pages/system-admin/SystemAdmin'));

// auth
export const authRoutes = {
  path: '/account',
  name: 'Auth',
  children: [
    {
      path: PagePaths.LOGIN_PATH,
      name: 'SignIn',
      component: SignIn,
      exact: true,
      route: Route,
    },
    {
        path: PagePaths.SIGN_UP_PATH,
        name: 'SignUp',
        component: SignUp,
        exact: true,
        route: Route,
    },
    {
      path: PagePaths.REGISTER_PATH,
      name: 'Register',
      component: Register,
      exact: true,
      route: Route,
    },
    {
      path: PagePaths.ERROR_PAGE_PATH,
      name: 'Error',
      component: ErrorPage,
      route: Route,
    },
    {
      path: PagePaths.LOGOUT_PATH,
      name: 'Logout',
      component: Logout,
      route: Route,
    },
    {
      path: PagePaths.FORGET_PASSWORD_PATH,
      name: 'Forgot Password',
      component: ForgetPassword,
      route: Route,
    },
    {
      path: PagePaths.FORGOT_PASSWORD_PATH,
      name: 'Forgot Password',
      component: ForgotPassword,
      route: Route,
    },
    {
      path: PagePaths.CONFIRM_ORGANIZATION_INVITATION,
      name: 'Confirm Organization Invitation',
      component: ConfirmOrganizationInvitation,
      route: Route,
    },
  ],
};

// root routes
export const rootRoute = {
  path: PagePaths.HOME_PATH,
  exact: true,
  component: () => <Redirect to={PagePaths.DASHBOARDS_PATH} />,
  route: PrivateRoute,
};

// root routes
export const landingRoute = {
  path: PagePaths.LANDING_PAGE_PATH,
  exact: true,
  name: 'Landing Page',
  icon: 'mdi mdi-laptop',
  component: Home,
  route: PrivateRoute,
};

const nonExistentOrganizationRoutes = {
  path: PagePaths.ORGANIZATION_NON_EXISTENT_ORGANIZATION_PATH,
  name: 'Organization',
  icon: 'uil-building',
  component: CreateOrganization,
  route: PrivateRoute,
  children: [
    {
      path: PagePaths.ORGANIZATION_CREATE_ORGANIZATION,
      name: 'Create',
      exact: true,
      component: CreateOrganizationRedirect,
      route: PrivateRoute,
    },
    {
      path: PagePaths.ORGANIZATION_REACTIVATE_PATH,
      name: 'Recover',
      exact: true,
      component: ReactivateOrganization,
      route: PrivateRoute,
    },
    {
      path: PagePaths.ORGANIZATION_LIST_ORGANIZATION_INVITES,
      name: 'Invites',
      exact: true,
      component: ListOrganizationInvites,
      route: PrivateRoute,
    },
  ],
};

const existentOrganizationRoutes = {
  path: PagePaths.ORGANIZATION_ORGANIZATION_PATH,
  name: 'Organization',
  icon: 'uil-building',
  component: OrganizationDashboard,
  route: PrivateRoute,
  children: [
    {
      path: PagePaths.ORGANIZATION_DASHBOARD_PATH,
      name: 'Dashboard',
      component: OrganizationDashboard,
      route: PrivateRoute,
      exact: true,
    },
    {
      path: PagePaths.ORGANIZATION_CLIENTS_PATH,
      name: 'Clients',
      component: ListOrganizationClients,
      route: PrivateRoute,
      exact: true,
    },
    {
      path: PagePaths.ORGANIZATION_USERS_PATH,
      name: 'Users',
      component: ListUsers,
      route: PrivateRoute,
      exact: true,
    },
    {
      path: PagePaths.ORGANIZATION_VALUE_PROPOSITIONS_PATH,
      name: 'Value Propositions',
      component: OrganizationValupPropositionList,
      route: PrivateRoute,
      exact: true,
    },
  ],
};

const dashboardRoutes = {
  path: PagePaths.DASHBOARDS_PATH,
  name: 'Dashboards',
  icon: 'uil-home-alt',
  header: 'Menu',
  component: Dashboard,
  exact: true,
  route: PrivateRoute,
};

const bulkOperationsRoutes = {
  path: PagePaths.BULK_OPERATIONS_PATH,
  name: 'Bulk Operations',
  icon: 'uil-cloud-upload',
  component: BulkOperations,
  exact: true,
  route: PrivateRoute,
};

const clientListRoutes = {
  path: PagePaths.CLIENT_LIST_PATH,
  name: 'Clients',
  icon: 'uil-bag-alt',
  component: ClientList,
  exact: true,
  route: PrivateRoute,
};

const valuePROPOSITIONSListRoutes = {
  path: PagePaths.VALUE_PROPOSITIONS_LIST_PATH,
  name: 'Value propositions',
  icon: 'uil-chart-pie-alt',
  component: ValuePropositionsList,
  exact: true,
  route: PrivateRoute,
};

const vpxRoutes = {
  path: PagePaths.VPX_LIST_PATH,
  name: 'VPX',
  icon: 'uil-exchange',
  children: [
    {
      path: PagePaths.VPX_LIST_PATH,
      name: 'Search',
      component: Vpxlist,
      exact: true,
      route: PrivateRoute,
    },
    {
      path: PagePaths.VPX_INVITATION_LIST_PATH,
      name: 'Followers',
      component: VpxInvitationList,
      exact: true,
      route: PrivateRoute,
    },
    {
      path: PagePaths.VPX_INVITATION_PUBLIC_PATH,
      name: 'Following - Public',
      component: VpxPublicInvitation,
      exact: true,
      route: PrivateRoute,
    },
    {
      path: PagePaths.VPX_INVITATION_PRIVATE_PATH,
      name: 'Following - Private',
      component: VpxPrivateInvitation,
      exact: true,
      route: PrivateRoute,
    },
  ],
};

export const internalProfileRoutes = [
  {
    path: PagePaths.PROFILE_PERSONAL_INFORMATION_PATH,
    name: 'Personal information',
    icon: 'uil-folder-check',
    component: PersonalInformation,
    exact: true,
    menuOption: MenuOptions.profile,
    route: PrivateRoute,
  },
  {
    path: PagePaths.PROFILE_SECURITY_PATH,
    name: 'Security',
    icon: 'uil-lock-alt',
    component: ProfileSecurity,
    exact: true,
    menuOption: MenuOptions.profile,
    route: PrivateRoute,
  },
  {
    path: PagePaths.PROFILE_SUBSCRIPTION_PATH,
    name: 'Subscription',
    icon: 'uil-clipboard-notes',
    component: Subscription,
    exact: true,
    menuOption: MenuOptions.profile,
    route: PrivateRoute,
  },
];

const profileRoutes = {
  path: internalProfileRoutes[0].path,
  name: 'Profile',
  icon: 'uil-user',
  component: internalProfileRoutes[0].component,
  exact: true,
  menuOption: MenuOptions.profile,
  route: PrivateRoute,
};

const inviteUsersRoutes = {
  path: PagePaths.ADMIN_INVITE_NEW_USERS,
  name: 'Invite Users',
  icon: 'uil-user-plus',
  component: InviteUsers,
  exact: true,
  route: PrivateRoute,
};

const systemAdminRoutes = {
  path: PagePaths.SYSTEM_ADMIN_PATH,
  name: 'System admin',
  icon: 'uil-shield-check',
  component: SystemAdmin,
  exact: true,
  route: PrivateRoute,
};

// Menu of prove it to me app.
export const menuRoutes = [
  dashboardRoutes,
  bulkOperationsRoutes,
  landingRoute,
  clientListRoutes,
  valuePROPOSITIONSListRoutes,
  profileRoutes,
  inviteUsersRoutes,
  systemAdminRoutes,
  nonExistentOrganizationRoutes,
  existentOrganizationRoutes,
  vpxRoutes,
];

export const internalRoutes = [
  {
    path: PagePaths.SUBSCRIPTION_EXPIRED_PATH,
    exact: true,
    component: SubscriptionExpiredPage,
    route: PrivateRoute,
  },
  {
    path: PagePaths.SHARED_VALUE_PROPOSITION_PATH,
    exact: true,
    component: SharedValueProposition,
    route: PublicAndPrivateRoute,
  },
  {
    path: PagePaths.VP_SHARE_LINK_PATH,
    exact: true,
    component: SharedValueProposition,
    route: PublicAndPrivateRoute,
  },
  {
    path: PagePaths.SHARED_USER_PROFILE_PATH,
    exact: true,
    component: SharedUserProfile,
    route: PublicAndPrivateRoute,
  },
  {
    path: PagePaths.ORGANIZATION_LICENSE_CHANGE_CONFIRM_PATH,
    exact: true,
    component: LicenseChangeConfirm,
    route: PrivateRoute,
  },
  {
    path: PagePaths.ORGANIZATION_ADD_ORGANIZATION_CLIENT_PATH,
    exact: true,
    component: AddOrganizationClient,
    route: PrivateRoute,
  },
  {
    path: PagePaths.ORGANIZATION_EDIT_ORGANIZATION_CLIENT_PATH,
    exact: true,
    component: EditOrganizationClient,
    route: PrivateRoute,
  },
  {
    path: PagePaths.ORGANIZATION_LICENSE_STRIP_PURCHASE_SUCCESS,
    exact: true,
    component: StripOrganizationPaymentSuccessFeedback,
    route: PrivateRoute,
  },
  {
    path: PagePaths.ORGANIZATION_STRIPE_PAYMENT_SUCCESS_PATH,
    exact: true,
    component: StripOrganizationPaymentSuccessFeedback,
    route: PrivateRoute,
  },
  {
    path: PagePaths.ORGANIZATION_LICENSE_STRIP_PURCHASE_CANCEL,
    exact: true,
    component: StripOrganizationPaymentCancelFeedback,
    route: PrivateRoute,
  },
  {
    path: PagePaths.ORGANIZATION_STRIPE_PAYMENT_CANCEL_PATH,
    exact: true,
    component: StripOrganizationPaymentCancelFeedback,
    route: PrivateRoute,
  },
  {
    path: PagePaths.ADD_CLIENT_PATH,
    exact: true,
    component: AddClient,
    route: PrivateRoute,
  },
  {
    path: PagePaths.EDIT_CLIENT_PATH,
    exact: true,
    component: EditClient,
    route: PrivateRoute,
  },
  {
    path: PagePaths.ADD_VALUE_PROPOSITION_PATH,
    exact: true,
    component: AddValueProposition,
    route: PrivateRoute,
  },
  {
    path: ROUTE_PATTERNS.EDIT_VP,
    exact: true,
    component: EditValueProposition,
    route: PrivateRoute,
  },
  // Old id-less path: redirect for one release, then remove.
  {
    path: PagePaths.EDIT_VALUE_PROPOSITION_PATH,
    exact: true,
    component: LegacyRedirect,
    route: PrivateRoute,
  },
  {
    path: ROUTE_PATTERNS.FUNDAMENTALS,
    exact: true,
    component: FundamentalsList,
    route: PrivateRoute,
  },
  {
    path: ROUTE_PATTERNS.ADD_FUNDAMENTAL,
    exact: true,
    component: AddFundamentals,
    route: PrivateRoute,
  },
  {
    path: ROUTE_PATTERNS.EDIT_FUNDAMENTAL,
    exact: true,
    component: EditFundamentals,
    route: PrivateRoute,
  },
  // Old id-less paths: redirect for one release, then remove.
  {
    path: PagePaths.FUNDAMENTALS_LIST_PATH,
    exact: true,
    component: LegacyRedirect,
    route: PrivateRoute,
  },
  {
    path: PagePaths.ADD_FUNDAMENTALS_PATH,
    exact: true,
    component: LegacyRedirect,
    route: PrivateRoute,
  },
  {
    path: PagePaths.EDIT_FUNDAMENTALS_PATH,
    exact: true,
    component: LegacyRedirect,
    route: PrivateRoute,
  },
  {
    path: PagePaths.PAYMENT_SUCCESS_PATH,
    exact: true,
    component: PaymentSuccess,
    route: PrivateRoute,
  },
  {
    path: PagePaths.ORGANIZATION_EDIT_ORGANIZATION_DETAILS_PATH,
    exact: true,
    component: EditOrganizationDetails,
    route: PrivateRoute,
  },
  {
    path: PagePaths.ORGANIZATION_CREATE_ORGANIZATION,
    exact: true,
    component: CreateOrganizationRedirect,
    route: PrivateRoute,
  },
  {
    path: PagePaths.VPX_USER_PROFILE_PATH,
    exact: true,
    component: VpxUserProfile,
    route: PrivateRoute,
  },
  {
    path: ROUTE_PATTERNS.USER_PROFILE,
    exact: true,
    component: VpxUserProfile,
    route: PrivateRoute,
  },
  {
    path: PagePaths.VPX_ORGANIZATION_PROFILE_PATH,
    exact: true,
    component: VpxOrganizationProfile,
    route: PrivateRoute,
  },
];
